<template>
    <div>
        <form @submit="submitUpdate">
            <div class="card" v-if="fieldTemplate">
                <div class="card-header">
                    <div class="card-header-title">
                        <span class="text-muted">Report Field Templates:&nbsp;</span> {{ fieldTemplate.name }}
                    </div>
                    <router-link :to="{ name: routes.Templates.FieldTemplateIndex }">
                        &laquo; Back to Field Templates
                    </router-link>
                </div>

                <div class="card-body">
                    <div class="row">
                        <div class="col-6">
                            <div class="form-group">
                                <label>Label</label>
                                <input type="text" class="form-control" v-model="fieldTemplate.label" required>
                            </div>

                            <div class="form-group">
                                <label>Category</label>
                                <input type="text" class="form-control" v-model="fieldTemplate.category">
                            </div>

                            <div class="is-comp-selector mb-2">
                                <label class="switch mr-1">
                                    <input type="checkbox" v-model="fieldTemplate.is_comparable">
                                    <span class="slider round"></span>                            
                                </label>
                                <span style="cursor: pointer;" @click="fieldTemplate.is_comparable = !fieldTemplate.is_comparable">
                                    Include this field in comparables view
                                </span>
                            </div>
                        </div>
                        <div class="col-6">
                            <div class="form-group">
                                <label>Input Type</label>
                                <select class="form-control" v-model="fieldTemplate.input_type">
                                    <option value="text">Text</option>
                                    <option value="date">Date</option>
                                    <option value="bool">Switch (Yes/No)</option>
                                    <option value="select">Picker Wheel</option>
                                    <option value="multi_select">Multi-Select</option>
                                </select>
                            </div>

                            <div class="form-group">
                                <label>Output Format</label>
                                <input type="text" class="form-control" v-model="fieldTemplate.output_format">
                            </div>
                        </div>
                    </div>

                    <div v-if="hasSelectOptions">
                        <div class="row mt-4">
                            <div class="col">
                                <h5>Field Options</h5>
                                <p class="mb-2">These are the options that will appear under this picker wheel or multi-select field.</p>

                                <div class="row select-option-row pt-2 pb-2" v-for="(opt, idx) in fieldTemplate.select_options" :key="idx">
                                    <div class="col-xs-12 col-sm-4">
                                        <div class="form-group">
                                            <label>Text</label>
                                            <input type="text" class="form-control form-control-sm" v-model="opt.label" required>
                                        </div>
                                    </div>
                                    <div class="col-xs-12 col-sm-7">
                                        <div class="form-group">
                                            <label>Output As</label>
                                            <input type="text" class="form-control form-control-sm" v-model="opt.output_as">
                                        </div>
                                    </div>
                                    <div class="col-xs-12 col-sm-1">
                                        <label>&nbsp;</label>
                                        <button class="btn btn-danger btn-block btn-sm" @click="removeSelectOption(idx)">
                                            <span class="mdi mdi-trash-can-outline"></span>
                                        </button>
                                    </div>
                                    <hr class="mt-0" v-if="fieldTemplate.select_options.length > 1 && idx < fieldTemplate.select_options.length - 1">
                                </div>

                                <div class="alert alert-warning col-8 offset-2 text-center mt-4" v-if="fieldTemplate.select_options.length == 0">
                                    <h5><span class="mdi mdi-information-outline"></span> No select options added</h5>
                                    <p>
                                        This field will appear in the app, but the picker wheel/multi-select field will be empty. 
                                        Click "Add Field Option" to start adding options to this field
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="row mt-4">
                            <div class="col text-center">
                                <button class="btn btn-primary" @click="fieldTemplate.select_options.push({ })">
                                    <span class="mdi mdi-plus"></span> Add Field Option
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- <pre>{{ fieldTemplate }}</pre> -->
                </div>

                <div class="card-footer">
                    <button class="btn btn-primary mr-2" :disabled="isUpdating || isDeleting">
                        <span class="mdi mdi-loading mdi-spin" v-if="isUpdating"></span>
                        <span class="mdi mdi-content-save-all-outline" v-if="!isUpdating"></span> Save Changes
                    </button>

                    <button class="btn btn-danger" @click="confirmDelete" :disabled="isDeleting || isUpdating">
                        <span class="mdi mdi-loading mdi-spin" v-if="isDeleting"></span>
                        <span class="mdi mdi-trash-can-outline" v-if="!isDeleting"></span> Delete
                    </button>
                </div>
            </div>
        </form>
        
        <LoadingIndicator v-if="viewState == 'Busy'" />
        <ErrorMessage v-if="viewState == 'Error'" />
    </div>
</template>

<script>
    import FieldTemplateService from '../../../services/templates/fieldTemplateService'
    import { RouteNames } from '../../../utils/constants'

    export default {
        props: {
            fieldId: String,
            fieldTemplateProp: {
                type: Object,
                default: null
            },
            isCreateResult: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                viewState: 'Idle',
                routes: RouteNames,
                error: null,
                fieldTemplate: null,
                isUpdating: false,
                isDeleting: false
            }
        },
        computed: {
            hasSelectOptions() {
                return this.fieldTemplate && 
                       (this.fieldTemplate.input_type == 'select' || this.fieldTemplate.input_type == 'multi_select')
            }
        },
        async mounted() {
            if (this.fieldTemplateProp) {
                this.fieldTemplate = this.fieldTemplateProp
                    
            }
            else {
                this.viewState = 'Busy'
                try {
                    this.fieldTemplate = await FieldTemplateService.getReportFieldTemplateByName(this.fieldId)
                    this.viewState = 'Idle'
                } catch (err) {
                    this.viewState = 'Error'
                    this.error = err
                }
            }
            
            if (this.isCreateResult) {
                this.$bvToast.toast('Field template created', {
                    title: 'Success',
                    variant: 'success',
                    solid: true,
                    appendToast: true,
                    autoHideDelay: 3000
                })
            }
        },
        methods: {
            removeSelectOption(idx) {
                this.$delete(this.fieldTemplate.select_options, idx)
            },
            confirmDelete() {

            },
            async submitUpdate(evt) {
                evt.preventDefault()
                this.isUpdating = true

                try {
                    // Label + value are the same thing
                    if (this.hasSelectOptions) {
                        this.fieldTemplate.select_options.forEach((o) => {
                            o.value = o.label
                        })
                    }

                    await FieldTemplateService.updateFieldTemplate(this.fieldId, this.fieldTemplate)
                    this.$bvToast.toast('Field template updated', {
                        title: 'Success',
                        variant: 'success',
                        solid: true,
                        appendToast: true,
                        autoHideDelay: 3000
                    })
                } catch (err) {
                    console.error(err)
                    this.$bvToast.toast('Sorry, something went wrong and we couldn\'t update the template', {
                        title: 'Error',
                        variant: 'danger',
                        solid: true,
                        appendToast: true,
                        autoHideDelay: 3000
                    })
                }
                this.isUpdating = false
            }
        }
    }
</script>

<style lang="scss" scoped>
    .select-option-row:nth-child(odd) {
        background: aliceblue;
    }
</style>